<template>
  <div>
    <v-dialog v-model="showModal" persistent max-width="600">
      <v-card justify="center" class="p-1">
        <v-card-title>Edit Release Notes
          <v-spacer/>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="save" ref="form" lazy-validation>
            <v-text-field
              v-model="releaseNotes.releaseId"
              :rules="[(v) => !!v || 'Release ID is required']"
              label="Release ID"
              outlined
              dense
              :error-messages="validationErrors.releaseId"
            />
            <v-menu
              ref="menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
              v-model="openDatePicker"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="pt-0"
                  v-model="releaseNotes.releaseDate"
                  label="Release Date"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules="[(v) => !!v || 'Release Date is required']"
                  outlined
                  dense
                  :error-messages="validationErrors.releaseDate"
                />
              </template>
              <v-date-picker
                class="white--text"
                no-title
                scrollable
                v-model="releaseNotes.releaseDate"
              >
                <v-spacer/>
                <v-btn
                  small
                  text
                  color="secondary"
                  @click="openDatePicker = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  small
                  text
                  color="secondary"
                  @click="openDatePicker = false"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
            <v-textarea
              v-model="releaseNotes.releaseNotes"
              auto-grow
              label="Release Notes"
              rows="3"
              outlined
              dense
              :error-messages="validationErrors.releaseNotes"
              max-height="100"
            />
            <v-btn color="secondary" type="submit">Save</v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <LoadingModal loadingText="Saving..." :showModal="isSubmitting" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import api from '@/common/api';
import LoadingModal from '@/common/components/LoadingModal.vue';
import { ReleaseNotes, initReleaseNotes } from '@/common/components/support/support.types';
import parse422 from '@/common/util/parseResponse.util';
import { initValidationErrors } from '@/common/validation/validation.types';

export default Vue.extend({
  props: {
    releaseNotesToEdit: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      openDatePicker: false,
      releaseNotes: initReleaseNotes() as ReleaseNotes,
      isSubmitting: false as boolean,
      showModal: false as boolean,
      validationErrors: initValidationErrors(),
    };
  },
  watch: {
    releaseNotesToEdit() {
      this.validationErrors = {};
      this.releaseNotes = this?.releaseNotesToEdit;
    },
    newReleaseNotes() {
      this.loadReleaseNotesData();
    },
  },
  methods: {
    loadReleaseNotesData() {
      if (this?.releaseNotes.releaseId === null
          || Object.keys(this?.releaseNotes).length === 0) return;
      this.showModal = true;
    },
    submitActions(isSubmitting: boolean) {
      this.isSubmitting = isSubmitting;
    },
    save() {
      if (!this.isValid()) {
        return;
      }
      this.submitActions(true);
      api
        .put(`/release-notes/${this?.releaseNotes?.id}`, this.releaseNotes)
        .then(() => {
          this.releaseNotes = initReleaseNotes();
          this.validationErrors = {};
          this.$emit('created');
          this.close();
          (this.$refs.form as any).resetValidation();
        })
        .catch(this.handleSaveError);
    },
    show() {
      this.showModal = true;
    },
    close() {
      this.submitActions(false);
      this.showModal = false;
    },
    isValid(): boolean {
      return (this.$refs.form as any).validate();
    },
    handleSaveError({ response }: { response: any }) {
      this.submitActions(false);
      const msg = parse422(
        response,
        `There was a problem saving the Release Notes. (${response.status})`,
      );
      this.$toasted.error(msg);
      this.validationErrors = response.data.errors || {};
    },
  },
  components: {
    LoadingModal,
  },
});
</script>
